import type { ReadonlyDeep } from 'type-fest'

import type { AnyStepContentElement } from '@flows/core/types/stepContentTypes'

const nonAutoSubmittableFormElementTypes = ['input', 'date', 'checkbox', 'phoneNumber', 'codeInput']
// 'spinner' is virtually a form element but not here since it doesn't have a name property
const formElementTypes = [
  'choice',
  'yesNo',
  'radioCardSet',
  'selectionButtonSet',
  'searchableList',
  'checkbox',
  'input',
  'codeInput',
  'date',
  'dropDown',
  'phoneNumber',
]

type StepContentElementLike = { readonly type: string; readonly shouldAutoSubmit?: boolean }

const isMultiSelectSearchableList = (element: StepContentElementLike) =>
  element.type === 'searchableList' &&
  (element as { readonly selectionMode?: string }).selectionMode === 'multiple'

const isCheckboxSelectionButtonSet = (element: StepContentElementLike) =>
  element.type === 'selectionButtonSet' &&
  'selectType' in element &&
  element.selectType === 'checkbox'

export const isNonAutoSubmittableFormElement = <T extends StepContentElementLike>(element: T) =>
  element &&
  (nonAutoSubmittableFormElementTypes.includes(element.type) ||
    isMultiSelectSearchableList(element) ||
    isCheckboxSelectionButtonSet(element))

export const isFormFieldElement = <T extends StepContentElementLike>(element: T) =>
  element && formElementTypes.includes(element.type)

const blocksAutoSubmit = (element: StepContentElementLike) =>
  isNonAutoSubmittableFormElement(element) || element.shouldAutoSubmit === false

export function shouldAutoSubmit<T extends ReadonlyDeep<AnyStepContentElement>>(
  content: readonly T[]
): boolean
export function shouldAutoSubmit(content: readonly StepContentElementLike[]): boolean
export function shouldAutoSubmit(content: readonly StepContentElementLike[]): boolean {
  const formElements = content.filter(isFormFieldElement)
  return formElements.length === 1 && !formElements.some(blocksAutoSubmit)
}
